/**
 * /unknowns — triage captcha khong khop profile nao (muc 8 va muc 12).
 *
 * DAY LA CHO AN TIEN KHI DUNG LAI CAPFORGE. Site moi cua project moi rat hay dung chung
 * mot thu vien captcha voi site cu. Neu chi nhin tung anh mot thi khong ai nhan ra dieu
 * do; nhung gom nhom theo pHash roi so voi profile da co thi no hien ra ngay: "nhom nay
 * giong securimage-default" — va the la dung lai duoc ca model lan tu dien, khong phai
 * gan nhan lai tu dau.
 *
 * Moi nhom gan nhu tuong ung mot generator captcha. So luong trong nhom cho biet nen uu
 * tien xu ly cai nao truoc.
 */

import { useEffect, useState } from 'react';
import type { ProfileDto, SolverInfo } from '@capforge/shared';
import { ErrorBox } from '../components/ErrorBox';
import { Modal } from '../components/Modal';
import { Spinner } from '../components/Spinner';
import { adminFetch, api, errorMessage, type UnknownGroupDto, type UnknownsResponse } from '../lib/api';
import { useSampleImage } from '../lib/useSampleImage';

/**
 * Khoang cach Hamming duoi muc nay thi coi la "rat giong" — dang tin de dung lai profile.
 * Tren muc nay van hien nhung noi ro la chi giong mo ho, de nguoi dung tu quyet.
 */
const CONFIDENT_DISTANCE = 10;

function Thumb({ sampleId }: { sampleId: number }): JSX.Element {
  const url = useSampleImage(sampleId);
  return (
    <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
      {url ? (
        <img src={url} alt={`mau ${sampleId}`} className="max-h-full max-w-full" />
      ) : (
        <span className="text-[10px] text-slate-400">#{sampleId}</span>
      )}
    </div>
  );
}


/**
 * Tao profile tu mot nhom captcha la (dac ta muc 8).
 *
 * ============================================================================
 *  DIEN SAN DUOC, NHUNG KHONG DUOC TU CHON THAY NGUOI DUNG.
 *
 *  Nhom anh la chi cho biet kich thuoc, taskType va pHash — tuc du de dien san
 *  `matchHints`. No KHONG cho biet nen dung solver nao: hai generator cho ra anh cung
 *  kich thuoc van co the mot cai la chu meo (ocr) con cai kia la luoi anh (hashgrid).
 *  Doan ho la dat mot lua chon sai vao tay nguoi dung duoi danh nghia tien loi.
 *
 *  Va `mode` luon la 'off' — server cung ep vay. Mot profile vua tao chua duoc kiem
 *  chung lan nao thi khong bao gio duoc phuc vu ngay; do la tinh than "khong bao gio
 *  tu dong thang cap" cua muc 11.
 * ============================================================================
 */
function CreateFromGroup({
  group,
  solvers,
  onClose,
  onCreated,
}: {
  group: UnknownGroupDto;
  solvers: SolverInfo[];
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [solver, setSolver] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (key.trim().length === 0) {
      setError('Phải đặt key cho profile.');
      return;
    }
    if (solver.length === 0) {
      setError('Phải chọn solver — hệ thống không đoán thay bạn.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await adminFetch<ProfileDto>('/admin/profiles', {
        method: 'POST',
        body: {
          key: key.trim(),
          label: label.trim().length > 0 ? label.trim() : 'Từ nhóm ' + group.phash.slice(0, 8),
          taskType: group.taskType,
          solver,
          // Dien san tu dac trung DO DUOC cua nhom. Kich thuoc la thu on dinh nhat de
          // khop mot generator; pHash thi khong dua vao day vi no doi theo tung anh.
          matchHints:
            group.width !== null && group.height !== null
              ? { imageWidth: group.width, imageHeight: group.height }
              : {},
        },
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open title="Tạo profile từ nhóm này" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {group.count} mau · {group.taskType}
          {group.width !== null && ' · ' + group.width + 'x' + group.height}
        </p>

        <div>
          <label className="cf-label" htmlFor="ng-key">
            Key (không dấu, dùng dấu gạch ngang)
          </label>
          <input
            id="ng-key"
            className="cf-input cf-mono"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="vd: site-abc-captcha"
          />
        </div>

        <div>
          <label className="cf-label" htmlFor="ng-label">
            Nhãn mô tả
          </label>
          <input
            id="ng-label"
            className="cf-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="để trống = tự đặt theo pHash"
          />
        </div>

        <div>
          <label className="cf-label" htmlFor="ng-solver">
            Solver
          </label>
          <select
            id="ng-solver"
            className="cf-input"
            value={solver}
            onChange={(e) => setSolver(e.target.value)}
          >
            <option value="">— chọn solver —</option>
            {solvers.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Nhóm ảnh không cho biết nên dùng solver nào — hai generator cho ra ảnh cùng kích
            thước vẫn có thể một cái là chữ méo, cái kia là lưới ảnh. Bạn chọn.
          </p>
        </div>

        {error !== null && (
          <p className="rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
            {error}
          </p>
        )}

        <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
          Profile mới luôn ở mode <strong>off</strong>. Bật lên ở trang Profile sau khi đã xem
          kết quả ở mode shadow.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600"
          >
            Huỷ
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {saving ? 'Đang tạo…' : 'Tạo profile'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GroupCard({
  group,
  solvers,
  onCreated,
}: {
  group: UnknownGroupDto;
  solvers: SolverInfo[];
  onCreated: () => void;
}): JSX.Element {
  const [creating, setCreating] = useState(false);
  const similar = group.similarProfile;
  const confident = similar !== null && similar.distance <= CONFIDENT_DISTANCE;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold">{group.count} mau</p>
          <p className="text-xs text-slate-500">
            {group.taskType}
            {group.width !== null && ` · ${group.width}x${group.height}`}
          </p>
          <p className="mt-1 font-mono text-[11px] text-slate-400">{group.phash}</p>
        </div>

        <div className="text-right text-sm">
          {similar === null ? (
            <p className="text-slate-500">
              Chưa profile nào giống.
              <br />
              <span className="text-xs">Đây là một generator mới — cần tạo profile riêng.</span>
            </p>
          ) : (
            <p className={confident ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-600'}>
              Trông giống <strong>{similar.key}</strong>
              <br />
              <span className="text-xs">
                khoảng cách {similar.distance}/64 bit
                {confident
                  ? ' — đáng tin, thử dùng lại profile này'
                  : ' — chỉ giống mơ hồ, hãy nhìn ảnh trước'}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {group.sampleIds.map((id) => (
          <Thumb key={id} sampleId={id} />
        ))}
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md border border-sky-600 px-3 py-1.5 text-sm font-medium text-sky-700 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-950"
        >
          Tạo profile từ nhóm này
        </button>
      </div>

      {creating && (
        <CreateFromGroup
          group={group}
          solvers={solvers}
          onClose={() => setCreating(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}

export function Unknowns(): JSX.Element {
  const [data, setData] = useState<UnknownsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Danh sach solver lay tu registry cua server, khong cam cung trong dashboard —
  // them mot solver moi phai tu hien ra o day (tinh chat 3).
  const [solvers, setSolvers] = useState<SolverInfo[]>([]);

  const load = (): void => {
    setLoading(true);
    setError(null);
    api
      .unknowns()
      .then(setData)
      .catch((err: unknown) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => {
    // Loi thi de danh sach rong: nguoi dung van xem duoc nhom, chi la chua tao duoc
    // profile — mot tinh nang phu hong khong duoc lam hong ca trang.
    api.solvers().then(setSolvers).catch(() => setSolvers([]));
  }, []);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Captcha lạ</h1>
        <p className="max-w-3xl text-sm text-slate-500 dark:text-slate-400">
          Mau khong khop profile nao, gom nhom theo pHash va kich thuoc. Moi nhom gan nhu
          tuong ung mot generator captcha. Cot ben phai cho biet nhom do co giong profile
          nao dang co khong — neu co, rat co the dung lai duoc model va tu dien san.
        </p>
      </header>

      {error && <ErrorBox message={error} onRetry={load} />}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : data && data.groups.length > 0 ? (
        <>
          <p className="text-sm text-slate-500">
            {data.groups.length} nhom tu {data.scanned} mau
            {data.scanned >= data.scanLimit && (
              <span className="text-amber-600">
                {' '}
                (da cham tran quet {data.scanLimit} mau — con mau cu hon chua duoc xet)
              </span>
            )}
          </p>
          <div className="grid gap-3 lg:grid-cols-2">
            {data.groups.map((g) => (
              <GroupCard key={g.phash} group={g} solvers={solvers} onCreated={load} />
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 py-12 text-center text-sm text-slate-500 dark:border-slate-600">
          <p>Không có captcha lạ nào.</p>
          <p className="mt-1">
            Moi mau di qua capforge deu khop mot profile — day la trang thai tot, khong phai
            man hinh hong.
          </p>
        </div>
      )}
    </div>
  );
}
